import React, { useState, useEffect, useCallback, useRef } from 'react';

const DURATION = 5000;

const PhotoCarousel = () => {
  const [photos] = useState([
    { id: 1, src: '/images/columbia-grad-1.png', caption: 'Columbia Graduation' },
    { id: 2, src: '/images/family-portrait-1.png', caption: 'Family Portrait' },
    { id: 3, src: '/images/family-portrait-2.png', caption: 'Tangram Mall' },
    { id: 4, src: '/images/marriage.png', caption: 'Our Wedding Day' },
    { id: 5, src: '/images/niagara-falls.png', caption: 'Niagara Falls Adventure' },
    { id: 6, src: '/images/harryL-drive.png', caption: 'Road Trip Memories' },
    { id: 7, src: '/images/greekpeak.png', caption: 'Greek Peak' },
    { id: 8, src: '/images/nathaniel.png', caption: 'Nathaniel' },
    { id: 9, src: '/images/xi\'an.png', caption: 'Xi\'an Adventures' },
    { id: 10, src: '/images/bower-rooftop.png', caption: 'Bower Rooftop' },
    { id: 11, src: '/images/revere-beach.png', caption: 'Revere Beach' },
    { id: 12, src: '/images/bu-graduation.png', caption: 'BU Graduation' },
    { id: 13, src: '/images/wedding-undertree.png', caption: 'Wedding Under Tree' },
    { id: 14, src: '/images/sanya-parkhyatt.png', caption: 'Sanya Park Hyatt' }
  ]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [progressKey, setProgressKey] = useState(0);
  const autoPlayRef = useRef(isAutoPlaying);
  autoPlayRef.current = isAutoPlaying;

  const nextPhoto = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
    setIsAutoPlaying(false);
  }, [photos.length]);

  const prevPhoto = useCallback(() => {
    setCurrentIndex((prev) => prev === 0 ? photos.length - 1 : prev - 1);
    setIsAutoPlaying(false);
  }, [photos.length]);

  useEffect(() => {
    if (isAutoPlaying) {
      setProgressKey(k => k + 1);
      const timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % photos.length);
      }, DURATION);
      return () => clearTimeout(timer);
    }
  }, [isAutoPlaying, currentIndex, photos.length]);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'ArrowLeft') {
        prevPhoto();
      } else if (event.key === 'ArrowRight') {
        nextPhoto();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [nextPhoto, prevPhoto]);

  const goToPhoto = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const toggleAutoPlay = () => {
    setIsAutoPlaying(prev => !prev);
  };

  return (
    <div className="photo-carousel">
      <div className="carousel-container">
        <div className="carousel-frame">
          {photos.map((photo, index) => (
            <div
              key={photo.id}
              className={`carousel-slide ${index === currentIndex ? 'active' : ''}`}
            >
              <img
                src={photo.src}
                alt={photo.caption}
                className={
                  photo.caption === 'Tangram Mall' ? 'tangram-photo' :
                  photo.caption === 'Our Wedding Day' ? 'wedding-photo' :
                  photo.caption === 'Nathaniel' ? 'nathaniel-photo' :
                  photo.caption === 'Xi\'an Adventures' ? 'xian-photo' :
                  photo.caption === 'Greek Peak' ? 'greekpeak-photo' :
                  photo.caption === 'Bower Rooftop' ? 'bower-photo' :
                  photo.caption === 'Revere Beach' ? 'revere-photo' :
                  photo.caption === 'Sanya Park Hyatt' ? 'sanya-photo' : ''
                }
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect width="100%" height="100%" fill="%23f8f9fa"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%23999" font-size="18">Photo ' + (index + 1) + '</text></svg>';
                }}
              />
              <div className="photo-frame-border"></div>
            </div>
          ))}
        </div>

        <button className="carousel-nav prev" onClick={prevPhoto}>
          <span>‹</span>
        </button>
        <button className="carousel-nav next" onClick={nextPhoto}>
          <span>›</span>
        </button>

        <div className="carousel-caption">
          {photos[currentIndex]?.caption}
        </div>
      </div>

      <div className="carousel-dots">
        {photos.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToPhoto(index)}
          />
        ))}
      </div>

      {isAutoPlaying && (
        <div className="current-progress-track">
          <div
            className="current-progress-fill"
            key={progressKey}
            style={{ animationDuration: `${DURATION}ms` }}
          />
        </div>
      )}

      <div className="carousel-controls">
        <button
          className={`auto-play-btn ${isAutoPlaying ? 'playing' : 'paused'}`}
          onClick={toggleAutoPlay}
        >
          {isAutoPlaying ? '⏸️' : '▶️'}
        </button>
      </div>
    </div>
  );
};

export default PhotoCarousel;